import { Cell } from '@pelckmans/elementis/components/table';
import DetailsIcon from '@pelckmans/elementis/icons/details';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAssignmentToDate } from '../../../../../utils/formatDate';
import { getResultPercentages, isQuantifiable } from '../../../../../utils/results';
import { Session } from '../../api/history';
import AbsoluteScoreCell from '../table/cells/AbsoluteScoreCell';
import DateCell from '../table/cells/DateCell';
import PercentageCell from '../table/cells/PercentageCell';
import TimeCell from '../table/cells/TimeCell';
import HistoryTable from './HistoryTable';

const BASE_PATH = 'PAGES.RESULTS.SESSION_HISTORY_MODAL';

interface Props extends Pick<ComponentProps<typeof HistoryTable>, 'isAssignment' | 'sessionsData' | 'scorableItem'> {
  horizontalScroll: boolean;
  onMaterialAccessed: (session: Session) => Promise<void>;
  link?: string;
}

export default function HistoryTableBody({
  sessionsData,
  isAssignment,
  scorableItem,
  link,
  onMaterialAccessed,
  horizontalScroll,
}: Props) {
  const { t } = useTranslation();

  return (
    <tbody>
      {sessionsData?.sessions.map((session, i) => {
        const quantifiable = isQuantifiable({
          score: session.score,
          unachievable: session.unachievable,
          status: session.status,
        });

        const needsGrading = session.lti?.gradingProgress === 'PendingManual';

        return (
          <tr key={`${session.session}-${i}`}>
            {isAssignment && session.assignment && (
              <Cell pillGroupProps={{ 'aria-label': undefined, role: 'group' }} minWidth="sm" maxWidth="sm">
                <Cell.Pill>
                  {t(`${BASE_PATH}.TABLE_VALUES.FROM`, { from: `${formatAssignmentToDate(session.assignment?.from)}` })}
                </Cell.Pill>
                <Cell.Pill>
                  {t(`${BASE_PATH}.TABLE_VALUES.TO`, { to: `${formatAssignmentToDate(session.assignment?.to)}` })}
                </Cell.Pill>
              </Cell>
            )}
            <DateCell
              completedBySystem={session.completedBySystem ?? false}
              isZeroScore={session.isZeroScore ?? false}
              achievedAt={session.achievedAt}
            />
            <Cell minWidth="xs">
              <Cell.Results
                {...getResultPercentages(session.score, session.lti?.gradingProgress === 'PendingManual')}
                quantifiable={quantifiable}
                status={session.status}
                showTooltip
                variant="donut"
              />
            </Cell>
            <PercentageCell
              achieved={quantifiable ? session.score.achieved : undefined}
              maximum={quantifiable ? session.score.maximum : undefined}
              needsGrading={needsGrading}
            />
            <AbsoluteScoreCell
              scorableItem={scorableItem}
              score={quantifiable ? session.score : undefined}
              needsGrading={needsGrading}
            />
            <TimeCell duration={session.duration} />
            <Cell
              buttonGroupProps={{
                'aria-label': t(`${BASE_PATH}.TABLE_VALUES.DETAILS`),
                role: 'group',
                className: 'button-group',
              }}
              className="actions-cell"
              sticky={horizontalScroll}
            >
              {link ? (
                <Cell.Button
                  as="link"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onMaterialAccessed(session)}
                  startIcon={<DetailsIcon />}
                  prominence="neutral"
                  aria-label={t(`${BASE_PATH}.TABLE_VALUES.DETAILS_BUTTON`)}
                />
              ) : (
                <Cell.Button
                  onClick={() => onMaterialAccessed(session)}
                  startIcon={<DetailsIcon />}
                  prominence="neutral"
                  aria-label={t(`${BASE_PATH}.TABLE_VALUES.DETAILS_BUTTON`)}
                />
              )}
            </Cell>
          </tr>
        );
      })}
    </tbody>
  );
}
